import React, { SetStateAction, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axios, { CancelToken } from "axios";

import { StatusCode } from "api/protocols";
import { IOrder } from "redux/reducers/models/order";

// import { useOrderFilters } from "hooks/useOrderFilters/useDropFilters";

import Card from "components/card/Card";
import Search from "components/search/Search";
import OrdersTable from "components/orderTable/OrdersTable";
// import { LocaleCurrency } from "helpers/Locale";

// import OrderFilters from "pages/secure/facility/order/OrderFilters";
import { useGuestPortalContext } from "pages/guest/Portal/GuestPortalContext";
import { GetOrder } from "api/rpc/2024-04/customer/customer";
import OrderFilterAccordion from "components/accordion/filterAccordions/OrderFilterAccordion";
import { DBFilterContext } from "redux/reducers/filters/filters";
import { filterContextIsEqual } from "components/accordion/Accordion";
import { useAppSelector } from "hooks/redux";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { TABLET_WIDTH } from "helpers/ScreenSizes";
import "./portalItemSales.scss";

export default function CustomerItemSales() {
  const { t } = useTranslation();

  //   const { state } = useCustomerContext();
  //   const orderFilters = useOrderFilters();
  const { portalState } = useGuestPortalContext();
  const guest = portalState.guest;
  const windowSize = useWindowSize();
  const today = new Date();

  const [orders, setOrders] = useState<IOrder[]>(undefined);
  const [activeFilters, setActiveFilters] = useState<DBFilterContext[]>(undefined);
  const bpFilters = useAppSelector(store => store.orderPageFilters);

  const [filterState, setFilterState] = useState({
    dateRangeStart: today,
    dateRangeEnd: today,
    search: "",
    limit: 50,
    offset: 0,
  });

  const [dateFilter, setDateFilter] = useState({
    start_date: "",
    end_date: "",
  });

  // Reload orders after search || offset changes.
  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadOrders(source.token);

    return () => {
      source.cancel();
    };
  }, [activeFilters, dateFilter]);

  useEffect(() => {
    if (activeFilters === undefined) {
      setActiveFilters(bpFilters);
    }
  }, [activeFilters]);

  async function loadOrders(token?: CancelToken) {
    if (orders !== undefined) {
      setOrders(undefined);
    }

    // remove unused filters from accordion
    let filterParams = activeFilters
      ?.filter(grouping => grouping.value.length !== 0)
      .map(ele => {
        return {
          label: ele.label,
          value: ele.value,
        };
      });

    // apply date filter if given
    if (dateFilter.start_date.length !== 0) {
      filterParams = filterParams.concat([
        { label: "start_date", value: [dateFilter.start_date] },
        { label: "end_date", value: [dateFilter.end_date] },
      ]);
    }

    const orderRes = await GetOrder(
      {
        customer_id: guest.id,
        filters: filterParams?.length > 0 ? filterParams : undefined,
      },
      true,
      token,
    );

    if (orderRes.status !== StatusCode.OK) {
      setOrders([]);
    } else {
      setOrders(orderRes.data as SetStateAction<IOrder[]>);
    }
  }

  function setDateRangeStart(date: Date) {
    setDateFilter(prevState => ({ ...prevState, start_date: date.toISOString().split("T")[0] }));

    setFilterState(prevState => ({
      ...prevState,
      dateRangeStart: date,
    }));
  }

  function setDateRangeEnd(date: Date) {
    setDateFilter(prevState => ({ ...prevState, end_date: date.toISOString().split("T")[0] }));

    setFilterState(prevState => ({
      ...prevState,
      dateRangeEnd: date,
    }));
  }

  return (
    <div className="mt-2 mb-4">
      <div className="portal-item-sales-filter-search-container">
        <div className="portal-item-sales-search-container">
          <Search
            historyKey={"customer-item-sales-search"}
            searchCallback={searchValue => setFilterState({ ...filterState, offset: 0, search: searchValue })}
            placeholder={t("Search")}
          />
        </div>

        <div className="portal-item-sales-filter-container ">
          <div className="w-full">
            <DatePickerInput
              months={windowSize.width > TABLET_WIDTH ? 2 : 1}
              position="right"
              startingDate={filterState.dateRangeStart}
              setStartingDate={setDateRangeStart}
              endingDate={filterState.dateRangeEnd}
              setEndingDate={setDateRangeEnd}
              showQuickOptions={windowSize.width > TABLET_WIDTH}
            />
          </div>

          <OrderFilterAccordion
            activeFilters={activeFilters}
            applyAllAction={updatedFilters => {
              setActiveFilters(updatedFilters);
            }}
            clearAllAction={updatedFilters => {
              if (updatedFilters.length === 0) {
                return undefined;
              }
              setActiveFilters(updatedFilters);
            }}
            allFiltersAreApplied={filterContextIsEqual(activeFilters, bpFilters)}
            rightAligned
            isLoading={activeFilters === undefined}
            paymentStatus
            registers={null}
            staff={null}
            paymentType={null}
            priceMatch
            creditCard
          />
        </div>
      </div>

      <Card>
        <Card.Section table="true">
          <OrdersTable
            orders={orders}
            tableLimit={filterState.limit}
            tableOffset={filterState.offset}
            handleTableOffset={direction =>
              setFilterState(prev => ({
                ...prev,
                offset: direction === "prev" ? prev.offset - prev.limit : prev.offset + prev.limit,
              }))
            }
            noClick
            facilityName
          />
        </Card.Section>
      </Card>
    </div>
  );
}
